import { KeyValue, KeyValuePipe, NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService
} from '@nebular/theme';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import JSZip from 'jszip';
import { DrbService } from 'src/app/features/drbs/services/drb.service';
import { Content, PayloadRecord } from 'src/app/models/content';
import { DrbValues } from 'src/app/models/drb-models/drb-values';
import { Xliff } from 'src/app/models/drb-models/xliff';
import { allLanguages } from '../../constants/translation-languages';

@Component({
  selector: 'app-preview-content',
  templateUrl: './preview-content.component.html',
  styleUrls: ['./preview-content.component.css'],
  imports: [
    NgIf,
    NgFor,
    NgSwitch,
    NgSwitchCase,
    NbSpinnerModule,
    NbIconModule,
    NbCardModule,
    NbButtonModule,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    KeyValuePipe
  ]
})
export class PreviewContentComponent implements OnInit {
  @Input() public content!: Content;
  public drbValues!: DrbValues;
  public isSpecialOffer = false;
  public loadingMessage = '';
  public isLoading = false;
  public button?: string | number | boolean;
  public buttonLink?: string | boolean;
  public elements?: Record<string, any>[];
  public language: keyof Xliff = 'source';
  public translationLanguages: Record<string, string> = allLanguages;

  constructor(
    protected ref: NbDialogRef<PreviewContentComponent>,
    private toastr: NbToastrService,
    private drbService: DrbService
  ) {}

  private get payloadRecords(): PayloadRecord[] {
    return this.content.payload_records ?? [];
  }

  public ngOnInit(): void {
    if (this.payloadRecords.length === 0) {
      this.toastr.danger('', $localize`Cannot show preview, content doesn't have any payload records.`);
      this.ref.close();
    }
    const drbIds = this.getDrbIds();
    if (drbIds.length > 0) {
      this.drbValues = this.drbService.getDrbValues(drbIds);
    }
    this.isSpecialOffer = this.payloadRecords[0].payload_record_type === 'personalized_offer';
    this.button = this.getActionButton();
    this.buttonLink = this.getButtonLink();
    this.elements = this.getElements();
    if (this.elements.length === 0) {
      this.toastr.danger('', $localize`Cannot show preview, content elements missing.`);
      this.ref.close();
    }
    this.loadImage();
  }

  public sortAlphabeticallyByValue(a: KeyValue<string, string>, b: KeyValue<string, string>): number {
    return a.value.localeCompare(b.value);
  }

  public getDrbIds(): string[] {
    const drbIds = [this.content.headline_text_id];
    const firstRecord = this.payloadRecords[0];
    if (firstRecord) {
      drbIds.push(firstRecord.image_id);
      drbIds.push(firstRecord.content_text_id);
      drbIds.push(firstRecord.content_html_id);
    }
    if (this.content.action_button && this.content.action_button.length > 0) {
      drbIds.push(this.content.action_button[0].text_id);
      drbIds.push(this.content.action_button[0].destination_uri_id);
    }
    return drbIds.filter((id) => id != undefined);
  }

  public languageChange(language: keyof Xliff) {
    this.button = this.getActionButton();
    this.elements = this.getElements();
    if (this.elements.length < 2) {
      this.elements.push({
        tag: 'warning',
        value: $localize`We cannot show the full Content Preview as some ${this.translationLanguages[language]} translations are missing.`,
        order: -1
      });
    }
    this.loadImage();
  }

  private async loadImage(): Promise<void> {
    const imageElement = this.elements?.find((el: any) => el.tag === 'img');
    if (!imageElement) {
      return;
    }

    try {
      this.isLoading = true;
      this.loadingMessage = $localize`Loading image...`;
      const newImage = await this.getImageFromZip(imageElement.url);

      imageElement.value = newImage;
      this.isLoading = false;
    } catch {
      imageElement.alt = $localize`Cannot load the image`;
      this.toastr.danger('', $localize`Image not found`);
      this.isLoading = false;
      this.loadingMessage = $localize`Failed to load image.`;
    }
  }

  private async getImageFromZip(imageUrl: string): Promise<any> {
    let file: any = null;
    const lastSlash = imageUrl.lastIndexOf('/');
    const url = imageUrl.slice(0, lastSlash);
    const imageName = imageUrl.slice(lastSlash + 1);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    zip.forEach((zipPath, zipFile) => {
      const zipPathArray = zipPath.split('/');
      const parentDir = zipPathArray[zipPathArray.length - 2];
      const fileName = zipPathArray[zipPathArray.length - 1];

      if (parentDir === 'drawable-xhdpi' && fileName === imageName) {
        file = zipFile;
      }
    });

    if (file) {
      const blob = await file.async('blob');
      const objectURL = URL.createObjectURL(blob);

      return objectURL;
    } else {
      throw new Error('Image not found in zip file.');
    }
  }

  public getImageUrlAndOrder() {
    if (this.payloadRecords.length === 0) return false;

    let imageUrl;
    const payloadRecord = this.payloadRecords[0];
    if (payloadRecord.image_id) {
      if (payloadRecord.image_id in this.drbValues['image']) {
        imageUrl = this.drbValues['image'][payloadRecord.image_id];
      } else {
        this.toastr.danger('', $localize`Couldn't find image value for Image ID in DRB`);
      }
    } else {
      imageUrl = payloadRecord.image_url;
    }
    if (imageUrl) {
      return {
        tag: 'img',
        value: '',
        isLoading: true,
        url: imageUrl,
        order: payloadRecord.image_display_order || 1
      };
    } else {
      return false;
    }
  }

  public getContentText() {
    if (this.payloadRecords.length === 0) return false;

    let contentText;
    const payloadRecord = this.payloadRecords[0];
    if (payloadRecord.content_text_id) {
      if (payloadRecord.content_text_id in this.drbValues.string) {
        contentText = this.drbValues.string[payloadRecord.content_text_id][this.language];
      } else {
        this.toastr.danger('', $localize`Couldn't find string value for Content Text ID in DRB`);
      }
    } else if (payloadRecord.content_text) {
      contentText = payloadRecord.content_text;
    }
    if (contentText) {
      return {
        tag: 'p',
        value: contentText,
        order: payloadRecord.context_text_display_order || 1
      };
    } else {
      return false;
    }
  }

  public getHeadlineText() {
    let headlineText;
    if (this.content.headline_text_id) {
      if (this.content.headline_text_id in this.drbValues['string']) {
        headlineText = this.drbValues['string'][this.content.headline_text_id][this.language];
      } else {
        this.toastr.danger('', $localize`Couldn't find string value for Headline Text ID in DRB`);
      }
    } else if (this.content.headline_text) {
      headlineText = this.content.headline_text;
    }
    if (headlineText) {
      return {
        tag: 'b',
        value: headlineText,
        order: 1
      };
    } else {
      return false;
    }
  }

  public getContentHtml() {
    if (this.payloadRecords.length === 0) return false;

    let contentHtml;
    const payloadRecord = this.payloadRecords[0];
    if (payloadRecord.content_html_id) {
      if (payloadRecord.content_html_id in this.drbValues['string']) {
        contentHtml = this.drbValues['string'][payloadRecord.content_html_id][this.language];
      } else {
        this.toastr.danger('', $localize`Couldn't find string value for Content HTML ID in DRB`);
      }
    } else if (payloadRecord.content_html) {
      contentHtml = payloadRecord.content_html;
    }
    if (contentHtml) {
      return {
        tag: 'p',
        value: contentHtml,
        order: payloadRecord.content_html_display_order || 1
      };
    } else {
      return false;
    }
  }

  public getActionButton() {
    const actionButton = this.content.action_button;
    if (actionButton && actionButton.length > 0) {
      if (actionButton[0] && actionButton[0].text_id) {
        if (actionButton[0].text_id in this.drbValues['string']) {
          return this.drbValues['string'][actionButton[0].text_id][this.language];
        } else {
          this.toastr.danger('', $localize`Couldn't find string value for Action Button Text ID in DRB`);
        }
      } else if (actionButton[0] && actionButton[0].text) {
        return actionButton[0].text;
      }
    }
    return false;
  }

  public getButtonLink() {
    const actionButton = this.content.action_button;
    if (actionButton && actionButton.length > 0) {
      if (actionButton[0] && actionButton[0].destination_uri_id) {
        if (actionButton[0].destination_uri_id in this.drbValues['link']) {
          return this.drbValues['link'][actionButton[0].destination_uri_id];
        } else {
          this.toastr.danger('', $localize`Couldn't find link value for Action Button URI ID in DRB`);
        }
      } else if (actionButton[0] && actionButton[0].destination_uri) {
        return actionButton[0].destination_uri;
      }
    }
    return false;
  }

  public getElements() {
    const elements = [];

    const image = this.getImageUrlAndOrder();
    if (image) {
      elements.push(image);
    }

    const headline = this.getHeadlineText();
    if (headline) {
      elements.push(headline);
    }

    const contentHtmlOrText = this.getContentHtml() || this.getContentText();
    if (contentHtmlOrText) {
      elements.push(contentHtmlOrText);
    }

    elements.sort((a, b) => a.order - b.order);
    return elements;
  }

  public close() {
    this.ref.close();
  }
}
